/**
 * FtpCampaignControl — per-campaign Delivery-FTP opt-in + catch-up, shown on
 * the campaign page when the current org has Delivery FTP enabled.
 *
 * Two jobs:
 *
 *  1. PRE-DELIVERY OPT-IN (when the campaign still has undelivered stations):
 *     - If the campaign's client is already on the org's FTP client list, this
 *       shows an auto-ticked, read-only "will be sent to FTP" note (the trigger
 *       sends it automatically on delivery).
 *     - If not on the list, shows a tickable "Send this campaign to your FTP".
 *       Ticking + delivering adds the client to the FTP list (via
 *       addDeliveryFtpClients) BEFORE the deliveries are created, so this
 *       delivery AND all future campaigns for that client auto-send. If the
 *       client looks similar to an existing list entry (shared word), a nudge
 *       recommends adding it.
 *
 *  2. POST-DELIVERY CATCH-UP (when the campaign has already been delivered):
 *     - If every deliverable code is already on the FTP → "Sent to FTP" badge.
 *     - Otherwise → a "Send to FTP now" button (pushCampaignToFtp). The backend
 *       claims against the job's ftpDeliveryState.sentCodes, so already-sent
 *       codes are never re-sent.
 *
 * The parent (JobDetail) calls the imperative `onBeforeDeliver()` right before
 * it creates the deliveries.
 */

import React, {
  forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState,
} from 'react';
import { FirebaseContext } from '../Firebase';
import { useToast } from '../../contexts/ToastContext';
import { Button, Spinner } from 'reactstrap';

export interface FtpCampaignControlHandle {
  /** Called by the parent immediately before deliveries are created. If the
   *  user opted in and the client isn't on the FTP list yet, adds it so the
   *  delivery trigger picks it up. Best-effort — never throws. */
  onBeforeDeliver: () => Promise<void>;
}

interface Props {
  orgId: string;
  jobId: string;
  /** Campaign client name (already normalized to a string). */
  client: string;
  /** Deliverable flighting codes for this campaign (from its deliveries). */
  codes: string[];
  /** Codes already pushed to the FTP (ftpDeliveryState.sentCodes). */
  sentCodes: string[];
  /** How many stations are still undelivered (drives the pre-delivery opt-in). */
  pendingCount: number;
  /** Whether the campaign has any prior delivery (drives the catch-up button). */
  hasPriorDelivery: boolean;
}

/** Similar = shares a word (≥3 chars) with a list entry but isn't an exact
 *  match. e.g. "SHOPRITE KWAZULU NATAL" ~ "SHOPRITE". Returns the first match. */
function findSimilar(client: string, list: string[]): string | null {
  const tokens = (s: string) => s.toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').split(/\s+/).filter((t) => t.length >= 3);
  const cTokens = new Set(tokens(client));
  for (const l of list) {
    if (tokens(l).some((t) => cTokens.has(t))) return l;
  }
  return null;
}

const FtpCampaignControl = forwardRef<FtpCampaignControlHandle, Props>((props, ref) => {
  const { orgId, jobId, client, codes, sentCodes, pendingCount, hasPriorDelivery } = props;
  const firebase: any = useContext(FirebaseContext);
  const { showSuccess, showError } = useToast();

  const [enabled, setEnabled] = useState<boolean | null>(null); // null = loading/unknown
  const [clients, setClients] = useState<string[]>([]);
  const [optIn, setOptIn] = useState(false);
  const [pushing, setPushing] = useState(false);

  // Load the org's Delivery FTP status + client allowlist.
  useEffect(() => {
    let cancelled = false;
    if (!firebase || !orgId) return;
    firebase.callFunction('checkFtpConfigStatus', { orgId, configType: 'delivery' })
      .then((res: any) => {
        if (cancelled) return;
        const d = res?.data || {};
        setEnabled(!!d.configured && d.active !== false);
        setClients(Array.isArray(d.clientFilter) ? d.clientFilter : []);
      })
      .catch((err: any) => {
        if (cancelled) return;
        console.error('[FtpCampaignControl] status load failed:', err);
        setEnabled(false);
      });
    return () => { cancelled = true; };
  }, [firebase, orgId]);

  const clientOnList = useMemo(
    () => !!client && clients.some((c) => c.toLowerCase() === client.toLowerCase()),
    [client, clients],
  );
  const similar = useMemo(
    () => (clientOnList || !client ? null : findSimilar(client, clients)),
    [clientOnList, client, clients],
  );

  // Default the opt-in tick to ON when the client is already on the list.
  useEffect(() => { setOptIn(clientOnList); }, [clientOnList]);

  const addClientIfNeeded = async () => {
    if (clientOnList || !client) return;
    await firebase.callFunction('addDeliveryFtpClients', { orgId, clients: [client] });
    setClients((prev) => (prev.some((c) => c.toLowerCase() === client.toLowerCase()) ? prev : [...prev, client]));
  };

  useImperativeHandle(ref, () => ({
    onBeforeDeliver: async () => {
      if (enabled !== true || clientOnList || !optIn || !client) return;
      try {
        await addClientIfNeeded();
      } catch (err: any) {
        console.error('[FtpCampaignControl] onBeforeDeliver add-client failed:', err);
        showError('Could not add the client to the FTP list — delivery will continue without FTP.');
      }
    },
  }), [enabled, clientOnList, optIn, client]); // eslint-disable-line react-hooks/exhaustive-deps

  const sentSet = useMemo(() => new Set((sentCodes || []).map((c) => c)), [sentCodes]);
  const unsent = useMemo(() => (codes || []).filter((c) => !sentSet.has(c)), [codes, sentSet]);
  const fullySent = (codes || []).length > 0 && unsent.length === 0;

  const handlePushNow = async () => {
    setPushing(true);
    try {
      await addClientIfNeeded();
      const res: any = await firebase.callFunction('pushCampaignToFtp', { orgId, jobId });
      const d = res?.data || {};
      if (d.failed > 0) {
        showError(`FTP: ${d.sent || 0} sent, ${d.failed} failed.`);
      } else if ((d.sent || 0) === 0) {
        showSuccess('Already on the FTP — nothing new to send.');
      } else {
        showSuccess(`Sent ${d.sent} file${d.sent === 1 ? '' : 's'} to FTP.`);
      }
    } catch (err: any) {
      console.error('[FtpCampaignControl] push now failed:', err);
      showError(err?.message || 'Failed to send to FTP.');
    } finally {
      setPushing(false);
    }
  };

  if (enabled !== true) return null; // FTP off / still loading → render nothing

  const showPre = pendingCount > 0;
  const showPost = hasPriorDelivery;
  if (!showPre && !showPost) return null;

  return (
    <div style={{
      margin: '0 0 1rem', padding: '10px 14px',
      backgroundColor: 'rgba(90, 170, 255, 0.06)', borderRadius: 6,
      border: '1px solid rgba(90, 170, 255, 0.15)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: (showPre || showPost) ? 6 : 0 }}>
        <i className="tim-icons icon-cloud-upload-94" style={{ color: '#5aaaff' }}></i>
        <span style={{ color: '#fff', fontWeight: 500, fontSize: '0.9rem' }}>Delivery FTP</span>
      </div>

      {/* PRE-DELIVERY opt-in */}
      {showPre && (
        clientOnList ? (
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.83rem' }}>
            <input type="checkbox" checked readOnly disabled style={{ marginRight: 8 }} />
            <strong style={{ color: '#fff' }}>{client}</strong> is on your FTP list — this campaign will be sent to the FTP on delivery.
          </div>
        ) : (
          <div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: 'rgba(255,255,255,0.8)', fontSize: '0.83rem', cursor: 'pointer', margin: 0 }}>
              <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                Send this campaign to your FTP{client ? <> (client <strong style={{ color: '#fff' }}>{client}</strong>)</> : ''}.
                Ticking adds this client to your FTP list for future campaigns too.
                {similar && (
                  <div style={{ color: '#ffb432', fontSize: '0.78rem', marginTop: 3 }}>
                    💡 “{client}” looks related to “{similar}” on your FTP list — you probably want it included.
                  </div>
                )}
              </span>
            </label>
          </div>
        )
      )}

      {/* POST-DELIVERY catch-up */}
      {showPost && (
        <div style={{ marginTop: showPre ? 8 : 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {fullySent ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              color: '#50c878', fontSize: '0.83rem', fontWeight: 500,
            }}>
              <i className="tim-icons icon-check-2"></i>
              Sent to FTP ({(codes || []).length})
            </span>
          ) : (
            <>
              <Button className="btn-primary-custom btn-round btn-sm" onClick={handlePushNow} disabled={pushing}>
                <span>{pushing ? <><Spinner size="sm" className="mr-1" /> Sending…</> : 'Send to FTP now'}</span>
              </Button>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}>
                {(codes || []).length === 0
                  ? 'No deliverable spots found for this campaign.'
                  : `${unsent.length} of ${(codes || []).length} not yet on the FTP.`}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
});

FtpCampaignControl.displayName = 'FtpCampaignControl';
export default FtpCampaignControl;
